import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  MessageCircle, Send, User, Bot, Lightbulb, 
  Scale, Map, FileText, Loader2, Sparkles
} from 'lucide-react';
import api from '../services/api';
import SubscriptionGate from './SubscriptionGate';

const TribalRightsGuru = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      message: "Greetings, seeker of knowledge. I am The Guru — an elder of law and legacy, a guide through the corridors of justice and sovereignty.\n\nI stand at the crossroads of Tribal American Law, Constitutional Law, English Common Law, Land and Property Law, and Equity Jurisprudence.\n\nI am here to teach, to empower, and to illuminate the path from bondage to birthright.\n\nWhat wisdom do you seek today?",
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);

  const samplePrompts = [
    "What is the difference between legal and lawful?",
    "How do I establish allodial title to my ancestral land?",
    "What are my treaty rights as a tribal member?",
    "Explain the Magna Carta and its relevance today",
    "What is equity jurisprudence?",
    "How does Article I Section 10 limit government power?",
    "What is the difference between a citizen and a sovereign?",
    "How can I use land patents to reclaim property rights?"
  ];

  const handleSendMessage = async (message = inputMessage) => {
    if (!message.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      message: message.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);
    setError(null);

    try {
      // Call the real OpenAI API through our backend
      const response = await api.post('/tribal-guru/chat', {
        message: message.trim(),
        conversation_history: messages.slice(-10).map(msg => ({
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.message,
          timestamp: msg.timestamp
        }))
      });

      const botResponse = {
        id: messages.length + 2,
        type: 'bot',
        message: response.data.response,
        timestamp: new Date(response.data.timestamp)
      };

      setMessages(prev => [...prev, botResponse]);
    } catch (err) {
      console.error('Guru chat error:', err);
      setError('I apologize, seeker. There seems to be a disturbance in our connection. Please try again.');
      
      // Add error message to chat
      setMessages(prev => [...prev, {
        id: prev.length + 1,
        type: 'bot',
        message: 'My apologies, seeker. A temporary obstacle prevents me from responding. Please try your question again, or seek the wisdom of our support team if this persists.',
        timestamp: new Date()
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <SubscriptionGate 
      feature="guru_access"
      title="The Guru (AI Guide)"
      description="Chat with The Guru — your AI elder guide through Tribal Law, Constitutional Rights, English Common Law, Land & Property Law, and Equity Jurisprudence"
    >
      <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-4">
          <Sparkles className="w-8 h-8 text-amber-600 mr-3" />
          <h2 className="text-3xl font-bold text-gray-900">Ask The Guru</h2>
          <Sparkles className="w-8 h-8 text-amber-600 ml-3" />
        </div>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Seek wisdom from The Guru — your elder guide through Tribal Law, Constitutional Rights, 
          English Common Law, Land & Property Law, and Equity Jurisprudence. 
          Powered by OpenAI GPT-4o.
        </p>
      </div>

      {/* Chat Interface */}
      <Card className="bg-white shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Sparkles className="w-5 h-5 mr-2 text-amber-600" />
            The Guru — Elder of Law & Legacy
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Messages */}
          <div className="h-96 overflow-y-auto border rounded-lg p-4 mb-4 bg-gray-50">
            <div className="space-y-4">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`flex max-w-[80%] ${msg.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.type === 'user' ? 'bg-blue-600 ml-3' : 'bg-green-600 mr-3'
                    }`}>
                      {msg.type === 'user' ? (
                        <User className="w-4 h-4 text-white" />
                      ) : (
                        <Bot className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div className={`p-3 rounded-lg ${
                      msg.type === 'user' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-white border border-gray-200'
                    }`}>
                      <p className="text-sm whitespace-pre-line">{msg.message}</p>
                      <p className={`text-xs mt-1 ${
                        msg.type === 'user' ? 'text-blue-100' : 'text-gray-500'
                      }`}>
                        {formatTimestamp(msg.timestamp)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              
              {isTyping && (
                <div className="flex justify-start">
                  <div className="flex">
                    <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center mr-3">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-white border border-gray-200 p-3 rounded-lg">
                      <div className="flex items-center space-x-1">
                        <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                        <span className="text-sm text-gray-500">Thinking...</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Input */}
          <div className="flex gap-2">
            <Input
              placeholder="Ask about your tribal rights..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              className="flex-1"
            />
            <Button 
              onClick={() => handleSendMessage()}
              disabled={!inputMessage.trim() || isTyping}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sample Prompts */}
      <Card className="bg-white shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Lightbulb className="w-5 h-5 mr-2 text-yellow-600" />
            Try These Common Questions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {samplePrompts.map((prompt, index) => (
              <Button
                key={index}
                variant="outline"
                className="justify-start text-left h-auto p-3 hover:bg-blue-50"
                onClick={() => handleSendMessage(prompt)}
              >
                <MessageCircle className="w-4 h-4 mr-2 text-blue-600 flex-shrink-0" />
                <span className="text-sm">{prompt}</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Topics Covered */}
      <Card className="bg-gradient-to-br from-green-50 to-blue-50 shadow-lg">
        <CardHeader>
          <CardTitle>Topics I Can Help With</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Scale className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Treaty Rights</h3>
              <p className="text-sm text-gray-600">
                Understanding historical treaties and how they apply to your modern rights
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Map className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Jurisdiction</h3>
              <p className="text-sm text-gray-600">
                Federal, state, and tribal authority questions and conflicts
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <FileText className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Legal Protections</h3>
              <p className="text-sm text-gray-600">
                Constitutional and statutory rights specific to tribal members
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Disclaimer */}
      <Card className="bg-yellow-50 border-l-4 border-yellow-400 shadow-lg">
        <CardContent className="p-4">
          <div className="flex items-start">
            <Lightbulb className="w-5 h-5 text-yellow-600 mr-3 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-800 mb-1">Educational Guidance Only</h3>
              <p className="text-yellow-700 text-sm">
                This AI assistant provides educational information about tribal rights and should not be considered 
                legal advice. For specific legal matters, always consult with qualified attorneys specializing in 
                federal Indian law or your tribal legal department.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </SubscriptionGate>
  );
};

export default TribalRightsGuru;